/**
 * @file static-domain.ts
 * @author Rapoo Device SDK Team
 * @date 2026-07-21
 * @description Telink 键盘静态灯效领域描述
 */

import {
  LightingDomainDescriptor,
  LightingDomainType,
  LightingFeatureFlag,
} from '../../../../../types/lightingDomain'
import { getAddress } from '../../../../../utils/common'
import { ADDRESS_TELINK } from '../../../protocols/address'
import { convertASCIIToStr } from '../../../../../utils/StringUtils'
import { sanitizeLightingLevel } from '../../../../shared/feature-impls/keyboard-lighting/lighting'

export const staticDomain: LightingDomainDescriptor = {
  type: LightingDomainType.Static,
  configSize: 8,
  supportedFeatures:
    LightingFeatureFlag.Brightness |
    LightingFeatureFlag.SingleColor |
    LightingFeatureFlag.Gradient |
    LightingFeatureFlag.KeyMap |
    LightingFeatureFlag.Name,

  addressResolver: {
    getBaseAddr: (_configIndex, itemIndex) => {
      return getAddress(ADDRESS_TELINK.STATIC_LIGHT_TYPE, itemIndex)
    },
    getFieldAddr: (field, _configIndex, itemIndex) => {
      // 查 Telink 自己的静态预设地址表，避免跨芯片借用地址
      const fieldAddressMap = {
        gradientDirection: ADDRESS_TELINK.STATIC_LIGHT_TYPE,
        brightness: ADDRESS_TELINK.STATIC_LIGHT_BRIGHTNESS,
        // 单色模式下 buffer[4] 是颜色类型位，对应渐变起始色 B 的地址（偏移 0x04）
        isColorful: ADDRESS_TELINK.STATIC_GRADIENT_COLOR_START_B,
      } as const
      const table =
        fieldAddressMap[field as keyof typeof fieldAddressMap] ?? ADDRESS_TELINK.STATIC_LIGHT_TYPE
      return getAddress(table, itemIndex)
    },
  },

  parser: {
    parse: (buffer, index) => ({
      index,
      domainType: LightingDomainType.Static,
      gradientDirection: buffer[0],
      brightness: sanitizeLightingLevel(buffer[1]),
      isColorful: buffer[4] === 0,
      rgbGroup: [2, 3].includes(buffer[0])
        ? [
            { r: buffer[2], g: buffer[3], b: buffer[4] },
            { r: buffer[5], g: buffer[6], b: buffer[7] },
          ]
        : undefined,
      rgb: [1].includes(buffer[0]) ? { r: buffer[5], g: buffer[6], b: buffer[7] } : undefined,
      buffer,
    }),
    serialize: (config) => {
      // 预置灯效的模式字节由固件写入，亮度更新时必须原样保留。
      const buf = config.buffer?.length === 8 ? new Uint8Array(config.buffer) : new Uint8Array(8)
      buf[0] = config.gradientDirection ?? 1
      buf[1] = sanitizeLightingLevel(config.brightness)
      if (buf[0] === 1) {
        if (!config.buffer || config.buffer.length !== 8) {
          buf[2] = 0xff
          buf[3] = 0xff
        }
        buf[4] = config.isColorful ? 0x00 : 0x01
        buf[5] = config.rgb?.r ?? 0xff
        buf[6] = config.rgb?.g ?? 0xff
        buf[7] = config.rgb?.b ?? 0xff
      } else if ([2, 3].includes(buf[0])) {
        const start = config.rgbGroup?.[0] ?? { r: 255, g: 255, b: 255 }
        const end = config.rgbGroup?.[1] ?? { r: 0, g: 0, b: 0 }
        buf[2] = start.r
        buf[3] = start.g
        buf[4] = start.b
        buf[5] = end.r
        buf[6] = end.g
        buf[7] = end.b
      }
      return buf
    },
  },

  hasCustomEffects: true,
  customEffectResolver: {
    nameAddress: ADDRESS_TELINK.STATIC_LIGHT_EFFECT_ADDRESS_NAME_LEVEL,
    readSize: 0x18,
    parseName: (data) => {
      const nameBuffer = new Array<string>(0x0c)
      for (let i = 0; i < 0x0c; i++) {
        nameBuffer[i] = data.getUint8(4 + i).toString(16)
      }
      const name = convertASCIIToStr(nameBuffer)
      return name || '0'
    },
    parseType: (data) => data.getUint8(4 + 0x0c),
  },

  cacheTags: (index, configIndex) => [`light:static:${index}`, `cfg:${configIndex}`],
}
